import { useEffect, useRef, useState } from 'react'

type State = 'omni' | 'repo'

const CARDS: { state: State; src: string; alt: string; label: string }[] = [
  {
    state: 'omni',
    src: '/images/pages/developers/hero-omni-card.svg',
    alt: 'One model change, drafted by the Modeling Agent in Omni on a branch, waiting for review',
    label: 'Show: in Omni',
  },
  {
    state: 'repo',
    src: '/images/pages/developers/hero-repo-card.svg',
    alt: 'The same model change, drafted by a coding agent with Omni agent skills on the same branch, pushed for review',
    label: 'Show: outside Omni',
  },
]

/** The pulse ring: the outline of the drawn card, the frame and the selected toggle pill as one shape, offset 2.5px
 *  outward so the 5px stroke sits entirely outside the drawing. Coordinates are the 602 x 464 export. */
const RINGS: Record<State, string> = {
  omni: 'M134.5 34.5H566.143A27.357 27.357 0 0 1 593.5 61.857V428.143A27.357 27.357 0 0 1 566.143 455.5H35.857A27.357 27.357 0 0 1 8.5 428.143V61.857A27.357 27.357 0 0 1 33.5 34.602V28A17.5 17.5 0 0 1 51 10.5H117A17.5 17.5 0 0 1 134.5 28Z',
  repo: 'M271.5 34.5H566.143A27.357 27.357 0 0 1 593.5 61.857V428.143A27.357 27.357 0 0 1 566.143 455.5H35.857A27.357 27.357 0 0 1 8.5 428.143V61.857A27.357 27.357 0 0 1 35.857 34.5H120.5V28A17.5 17.5 0 0 1 138 10.5H254A17.5 17.5 0 0 1 271.5 28Z',
}

const CYCLE_MS = 4500
const PULSE_MS = 420

/** The hero artifact: one card that toggles between the Modeling Agent in Omni and a coding agent outside it.
 *  Both faces are the owner's exports shown as drawn; this adds only the crossfade, the pulse, and the hit areas
 *  over the drawn toggle. The frame crossfades; the toggle row (a second, clipped copy of each face) switches at
 *  once, so the pills never fade (owner call, 2026-09-11). It cycles until touched. */
export const HeroCard = () => {
  const [state, setState] = useState<State>('omni')
  const [pulse, setPulse] = useState(false)
  const touched = useRef(false)
  const hovering = useRef(false)

  const show = (next: State) => {
    setState((current) => {
      if (current === next) return current
      setPulse(true)
      return next
    })
  }

  useEffect(() => {
    if (!pulse) return
    const t = setTimeout(() => setPulse(false), PULSE_MS)
    return () => clearTimeout(t)
  }, [pulse])

  useEffect(() => {
    const timer = setInterval(() => {
      if (touched.current || hovering.current) return
      setState((current) => {
        setPulse(true)
        return current === 'omni' ? 'repo' : 'omni'
      })
    }, CYCLE_MS)
    return () => clearInterval(timer)
  }, [])

  return (
    <div
      className="card-art"
      onMouseEnter={() => (hovering.current = true)}
      onMouseLeave={() => (hovering.current = false)}
    >
      {CARDS.map((card) => (
        <img
          key={card.state}
          className={card.state === state ? 'full on' : 'full'}
          src={card.src}
          alt={card.alt}
          width={602}
          height={464}
          draggable={false}
        />
      ))}
      {/* the toggle row again, clipped to the pills: it switches at once while the frame below crossfades */}
      {CARDS.map((card) => (
        <img
          key={`${card.state}-tabs`}
          className={
            card.state === state ? 'full full--tabs on' : 'full full--tabs'
          }
          src={card.src}
          alt=""
          aria-hidden="true"
          width={602}
          height={464}
          draggable={false}
        />
      ))}
      <svg
        className={pulse ? 'pulse go' : 'pulse'}
        viewBox="0 0 602 464"
        aria-hidden="true"
      >
        <path d={RINGS[state]} />
      </svg>
      {CARDS.map((card) => (
        <button
          key={card.state}
          className="hit"
          data-state={card.state}
          type="button"
          aria-label={card.label}
          aria-pressed={card.state === state}
          onClick={() => {
            touched.current = true
            show(card.state)
          }}
        />
      ))}
    </div>
  )
}
